import { RawDraftContentState } from 'draft-js'
import { enumNarrow } from '../helpers/enumGuard'
import {
  IBorrowingBase,
  IOngoingReporting,
  IFlags,
  OngoingReportingType,
  ReportingPeriods,
} from './bbc'
import { IBBCLog } from './bbcLog'
import { IClientInfo } from './client'
import { ICommentary } from './commentary'
import { IDueDiligence } from './dueDiligence'
import { IEntityInfo } from './entityInfo'
import { IOPSReporting } from './prospects'
import { IUser } from './user'

export enum WorkflowTypes {
  bbc = 'Borrowing Base',
  ongoingReporting = 'Ongoing Reporting',
  opsFlow = 'Ops Flow',
  entityPage = 'Entity Page',
  clientPage = 'Client Page',
  dueDiligencePage = 'Due Diligence Page',
  homePage = 'Home Page',
  clientManagement = 'Client Management',
  prospectsList = 'Prospects List',
  companyPage = 'Company Page',
  personPage = 'Person Page',
  organizationPage = 'Organization Page',
  // Replies on a single market news headline. Deliberately absent from
  // `workflowMappings`: the thread is opened from the headline itself, not from
  // the page-wide notes column, which has no headline to scope itself to.
  marketNews = 'Market News',
  // Team notes on one meeting (event). Opened from the meeting, never synced to Google.
  meeting = 'Meeting',
}

export const CRM_WORKFLOW_TYPES = [
  WorkflowTypes.companyPage,
  WorkflowTypes.personPage,
  WorkflowTypes.organizationPage,
]

export const isCrmWorkflow = (workflow: WorkflowTypes): boolean =>
  CRM_WORKFLOW_TYPES.includes(workflow)

export const Pages = {
  bbc: 'BBC',
  financials: 'Financials',
  salesBySKU: 'Sales By SKU',
  projections: 'Projections',
  arGeneralLedger: 'AR General Ledger',
  clientPage: 'Client Page',
  ops: 'OPS',
  dueDiligence: 'Due Diligence',
  entity: 'Entity',
  capTable: 'Cap Table',
}

export enum NotesFilterOptions {
  Pinned = 'Pinned',
  Open = 'Open',
  CallPrep = 'Call Prep',
  Resolved = 'Resolved',
  CallAgenda = 'Call Agenda',
  Archived = 'Waived',
  All = 'All',
  ActionRequired = 'Action Required',
}

/**
 * Narrow a quick-filter value (which may be a NotesFilterOptions member OR a
 * free user-defined note tag) to a NotesFilterOptions — or undefined when it's a
 * tag. Lets call sites compare against the enum without an unsound `as` cast.
 */
export const asNotesFilterOption = enumNarrow(NotesFilterOptions).as

export const GENERAL_NOTES_FILTER_OPTIONS = [
  NotesFilterOptions.All,
  NotesFilterOptions.Pinned,
  NotesFilterOptions.Open,
  NotesFilterOptions.Resolved,
]

export const DD_NOTES_FILTER_OPTIONS = [
  NotesFilterOptions.Open,
  NotesFilterOptions.CallAgenda,
  NotesFilterOptions.ActionRequired,
  NotesFilterOptions.All,
  NotesFilterOptions.Resolved,
  NotesFilterOptions.Archived,
].filter((option) => option !== NotesFilterOptions.Pinned)

export const workflowMappings = {
  bbc: WorkflowTypes.bbc,
  'ongoing-reporting': WorkflowTypes.ongoingReporting,
  prospects: WorkflowTypes.opsFlow,
  entity: WorkflowTypes.entityPage,
  clients: WorkflowTypes.clientPage,
  'client-setup': WorkflowTypes.clientPage,
  'due-diligence': WorkflowTypes.dueDiligencePage,
  'client-management': WorkflowTypes.clientManagement,
  // `prospects-list` is the BD prospects queue, a list page like
  // `client-management` — not to be confused with `prospects`, the per-prospect
  // OPS flow above. Without this key the route resolves to no workflow at all,
  // which is what silently removed the whole right column (and with it the only
  // entry point to Market News) when BD users stopped landing on `/`.
  'prospects-list': WorkflowTypes.prospectsList,
  organization: WorkflowTypes.organizationPage,
}

export const crmWorkflowMappings = {
  companies: WorkflowTypes.companyPage,
  people: WorkflowTypes.personPage,
  organizations: WorkflowTypes.organizationPage,
}

export const opsWorkflowToSalesforceFieldMapping = {
  receivables: 'Latest_AR_Comments__c',
  payables: 'Latest_AP_Comments__c',
  inventory: 'Latest_Inventory_Comments__c',
  'income-statement': 'Latest_Financial_Comments__c',
  'balance-sheet': 'Latest_Financial_Comments__c',
  'income-statement-projections': 'Latest_Financial_Comments__c',
  'balance-sheet-projections': 'Latest_Financial_Comments__c',
  summary: 'General_Comments__c',
  committee: 'Questions_for_Committee__c',
}
export interface INoteMention {
  id: string
  entityId: string
  noteId: string
  entity?: IEntityInfo
}
export interface IAttachment {
  fileName: string
  contentType: string
  attachmentSrc: string
}

export interface ITranscript {
  id: string
  date: Date
  transcriptUrl: string
  title?: string | null
}

export interface IFirefliesSentence {
  id: string
  startTime: number
  transcript: ITranscript
  /** The line the entry was drawn from — previewed on hover over the call chip. */
  text?: string | null
  textCleanup?: string | null
}

export interface ITag {
  id: string
  tag: string
  firefliesSentence: IFirefliesSentence
}

export interface INote {
  id: string
  workflow: string
  note: RawDraftContentState
  noteText: string
  flagId: string
  flags: IFlags
  bbcLog: IBBCLog[]
  entityFlagId: string
  clientName: string
  createdAt: Date
  resolved: boolean
  pinned: boolean
  user: IUser
  children: INote[]
  parent?: INote
  /**
   * The thread's root note. Not a column — attached by the notification paths
   * so a deep reply's notification can still resolve the commentary box its
   * thread hangs off (`common/helpers/notificationRouting.ts`).
   */
  threadRoot?: INote | null
  noteMentions: INoteMention[]
  borrowingBase: IBorrowingBase
  /**
   * Set instead of `borrowingBase` once the BBC the note was taken on has been
   * deleted — enough to keep showing its tag, with nothing to link to.
   */
  deletedBorrowingBaseRecordDate?: string | null
  deletedBorrowingBaseIsTest?: boolean | null
  opsReporting: IOPSReporting
  ongoingReporting: IOngoingReporting
  ddInfo: IDueDiligence
  clientInfo: IClientInfo
  attachments: IAttachment[]
  updatedAt?: Date
  isEdited?: boolean
  isNoteTextEdited?: boolean
  noteTextUpdatedAt?: string
  isArchived?: boolean
  isCallAgenda?: boolean
  isFlagged?: boolean
  // True while Arc is working this thread after being @-tagged in it (root note
  // only). Drives the "Arc is working…" loading indicator; flipped live via the
  // ATLAS_AI_NOTE_WORKING websocket event.
  isArcWorking?: boolean
  /**
   * Whether the requesting user has yet to read this note. Stamped per-reader by
   * the server, so it is meaningless in any other context.
   */
  isUnread?: boolean
  tags: ITag[]
  dragOrder: number
  commentaryId: string | null
  commentary?: ICommentary | null
  parentId: string | null
  cellNoteId?: string
  cellNote?: ICellNote | null
  companyId?: string | null
  personId?: string | null
  organizationId?: string | null
}

export interface INotesData {
  data: INote[]
  total: number
  distinctTags: string[]
  dataByTags: Record<NotesTags, INote[]>
  listId?: string
  /** Client-side stamp of the cell the list was fetched for (see `getCellNotesListKey`). */
  cellListKey?: string
}

/**
 * Counts the collapsed notes rail needs on page load. `needsAction` is driven by
 * unresolved state (action-required and not resolved), so viewing the panel never
 * clears it — only resolving the note does.
 */
export interface INotesCount {
  total: number
  needsAction: number
  /**
   * Notes this user hasn't read yet — notification-style, so reading clears it.
   * Deliberately distinct from `needsAction`, which only resolving clears.
   */
  unread: number
}

export enum NoteInlineStyle {
  Bold = 'BOLD',
  Italic = 'ITALIC',
  Underline = 'UNDERLINE',
  Code = 'CODE',
  Strikethrough = 'STRIKETHROUGH',
  HighlightYellow = 'HIGHLIGHT_YELLOW',
  HighlightGreen = 'HIGHLIGHT_GREEN',
  HighlightRed = 'HIGHLIGHT_RED',
  HighlightBlue = 'HIGHLIGHT_BLUE',
  HighlightPurple = 'HIGHLIGHT_PURPLE',
}

export enum NoteBlockType {
  Unstyled = 'unstyled',
  HeaderOne = 'header-one',
  HeaderTwo = 'header-two',
  UnorderedListItem = 'unordered-list-item',
  OrderedListItem = 'ordered-list-item',
}

/**
 * The block types the task/channel rich-text rule accepts — the Note types plus
 * Draft's `atomic`, which backs the inline images and GIFs those editors can
 * embed without being one of them.
 *
 * Shared because both ends of that rule have to name the same set: the server
 * validates against it (taskDescriptionValidation) and the editors coerce to it
 * (sanitizeRawContent). A list that drifts is a message the composer accepts and
 * the server rejects.
 *
 * Deliberately not the rule in notes/Add.ts, which is the bare NoteBlockType set:
 * the note editor uploads a pasted image as an attachment rather than embedding
 * it, so it never produces an atomic block and has no reason to accept one.
 */
export const RICH_TEXT_BLOCK_TYPES: string[] = [...Object.values(NoteBlockType), 'atomic']

export const NOTE_HIGHLIGHT_STYLES: NoteInlineStyle[] = [
  NoteInlineStyle.HighlightYellow,
  NoteInlineStyle.HighlightGreen,
  NoteInlineStyle.HighlightRed,
  NoteInlineStyle.HighlightBlue,
  NoteInlineStyle.HighlightPurple,
]

export interface INoteMapping {
  id: string
  noteParentId: string | null
  clientName: string
  clientId: string
  clientIconUrl: string
  noteText: string
  createdAt: string
  workflow: string
  entities: string[]
  userFirstName: string
  userLastName: string
  borrowingBaseId: string
  borrowingBaseRecordDate: string
  opsReportingId: string
  ongoingReportingId: string
  ongoingReportingType: string
  ongoingReportingRecordDate: string
}

export enum NotesTags {
  All = 'All',
  CorporateStructure = 'Corporate Structure',
  HistoricalIncomeStatement = 'Historical Income Statement',
  HistoricalBalanceSheet = 'Historical Balance Sheet',
  Forecast = 'Forecast',
  AccountsReceivable = 'Accounts Receivable',
  AccountsPayable = 'Accounts Payable',
  Inventory = 'Inventory',
  GeneralCompany = 'General Company',
  EquityRaises = 'Equity Financing & Investors',
  DebtFinancing = 'Current & Prior Debt',
  AccountingLegal = 'Accounting & Legal',
}

export const TAG_ORDER: string[] = [
  NotesTags.HistoricalIncomeStatement,
  NotesTags.HistoricalBalanceSheet,
  NotesTags.Forecast,
  NotesTags.AccountsReceivable,
  NotesTags.AccountsPayable,
  NotesTags.Inventory,
  NotesTags.EquityRaises,
  NotesTags.DebtFinancing,
  NotesTags.CorporateStructure,
  NotesTags.AccountingLegal,
  NotesTags.GeneralCompany,
]

export const TAG_COLOR_MAPPING: Partial<
  Record<NotesTags, { backgroundColor: string; color: string }>
> = {
  [NotesTags.CorporateStructure]: {
    backgroundColor: 'var(--notes-tag-corporate-bg)',
    color: 'var(--notes-tag-corporate-text)',
  },
  [NotesTags.HistoricalIncomeStatement]: {
    backgroundColor: 'var(--notes-tag-income-bg)',
    color: 'var(--notes-tag-income-text)',
  },
  [NotesTags.HistoricalBalanceSheet]: {
    backgroundColor: 'var(--notes-tag-balance-bg)',
    color: 'var(--notes-tag-balance-text)',
  },
  [NotesTags.Forecast]: {
    backgroundColor: 'var(--notes-tag-forecast-bg)',
    color: 'var(--notes-tag-forecast-text)',
  },
  [NotesTags.AccountsReceivable]: {
    backgroundColor: 'var(--notes-tag-receivable-bg)',
    color: 'var(--notes-tag-receivable-text)',
  },
  [NotesTags.AccountsPayable]: {
    backgroundColor: 'var(--notes-tag-payable-bg)',
    color: 'var(--notes-tag-payable-text)',
  },
  [NotesTags.Inventory]: {
    backgroundColor: 'var(--notes-tag-inventory-bg)',
    color: 'var(--notes-tag-inventory-text)',
  },
  [NotesTags.GeneralCompany]: {
    backgroundColor: 'var(--notes-tag-general-bg)',
    color: 'var(--notes-tag-general-text)',
  },
  [NotesTags.EquityRaises]: {
    backgroundColor: 'var(--notes-tag-equity-bg)',
    color: 'var(--notes-tag-equity-text)',
  },
  [NotesTags.DebtFinancing]: {
    backgroundColor: 'var(--notes-tag-debt-bg)',
    color: 'var(--notes-tag-debt-text)',
  },
  [NotesTags.AccountingLegal]: {
    backgroundColor: 'var(--notes-tag-legal-bg)',
    color: 'var(--notes-tag-legal-text)',
  },
}

export interface ICellNote {
  id: string
  tableType: CellNoteTableNames
  rowId: string
  columnId: string
  recordDate?: string
  clientName?: string
  ongoingReportingId?: string
  opsReportingId?: string
  borrowingBaseId?: string
  dueDiligenceId?: string
  note: INote
  value: string
  createdAt: string
  updatedAt: string
}

// Lightweight cell note indicator for efficient table loading
export interface ICellNoteIndicator {
  rowId: string
  columnId: string
  recordDate?: string | null
  noteCount: number
  lastUpdated: Date
  hasUnresolved: boolean
  cellNoteId: string
  /**
   * Pending note->cell suggestions on this cell. Drives the review signal: a hollow dashed
   * ring when the cell has nothing else, or an accent on the solid marker when it also has a
   * real note — so a note can never hide something waiting to be reviewed.
   */
  suggestedCount: number
  /** Established facts (amendment/rule) attributed to this cell. Authoritative, view-only. */
  establishedCount: number
  /**
   * How many of this cell's notes came from an amendment rather than a person. Apart from
   * `establishedCount` because these are already inside `noteCount` — summing both is what
   * makes a marker read "2" over one note. Drives the marker's colour, nothing else.
   */
  establishedNoteCount: number
  /**
   * Suggestions accepted onto this cell. Counted alongside real notes for the solid marker:
   * approving a suggestion has to flip the cell from the dashed ring to the solid marker,
   * and a confirmed link is the only thing that changes when it does.
   */
  confirmedLinkCount: number
}

// Response structure for cell note indicators
export interface ICellNoteIndicatorsResponse {
  id: string
  indicators: ICellNoteIndicator[]
  indicatorsByCell: Record<string, ICellNoteIndicator>
  count: number
}

export interface CellNoteConfig {
  rowId?: string
  columnId?: string
  value?: string
  recordDate?: string | null
  align?: 'left' | 'right'
  isTableFilterRowCell?: boolean
  canExpand?: boolean
  isExpanded?: boolean
  onExpandFields?: () => void
  onCollapseFields?: () => void
  isGlobalColumn?: boolean
  // Optional entity IDs to link the note to entities via Note_mentions
  entityId?: string
  clientId?: string
  userId?: string
  prospectEntityId?: string
}

export interface TableNoteConfig {
  tableType: CellNoteTableNames | string
  clientId: string
  id: string
  workflow: WorkflowTypes
  rowIds?: string[]
  columnIds?: string[]
  globalColumnIds?: string[]
  recordDate?: string | string[]
  recordDateByColumnId?: Record<string, string>
  enabled?: boolean
  /** When set, lookups for cell notes include rolled-up columns (e.g. quarterly column shows notes from its months). */
  periodView?: ReportingPeriods
}

export interface CellCoordinates {
  tableType: string
  rowId: string
  columnId: string
}

/** Non-human origin of an auto-created note, stored on `notes.source`. */
export enum NoteSource {
  Fireflies = 'Fireflies',
}

/**
 * What an Arc pipeline wrote a note as, stored on `notes.arc_note_kind`. NULL on
 * every human-authored note.
 *
 * The kind exists to give a pipeline's own writes different lifetimes, so a rerun
 * knows what it may replace and what it must leave alone. A pipeline that needs a
 * distinction of its own adds a value here rather than a column.
 *
 * The values below belong to the diligence-call pass
 * (services/fireflies/writeDiligenceAnswers): an Answer records what was said
 * on one call and is never rewritten — a later call adds its own — while an
 * Outstanding entry is a live to-do the next call replaces wholesale, so anything
 * since answered stops showing.
 */
export enum ArcNoteKind {
  /** What the borrower said on the call, filed under the question it answers. */
  Answer = 'answer',
  /** Parts of the question still open, replaced on each call. */
  Outstanding = 'outstanding',
  /** Asked on the call, no answer given. */
  AskedNoAnswer = 'asked_no_answer',
  /** A question the call raised and answered that was not on the list at all. */
  CapturedQuestion = 'captured_question',
}

export enum CellNoteTableNames {
  IncomeStatement = 'P&L',
  BalanceSheet = 'BS',
  CashFlow = 'Cash Flow',
  ARSummary = 'AR',
  ARRollforward = 'AR Roll',
  APRollforward = 'AP Roll',
  APSummary = 'AP',
  InventorySummary = 'Inventory',
  DilutionSummary = 'Dilution',
  DilutionOverTime = 'Dilution OT',
  InventoryDistribution = 'Inv. by',
  SalesBySKU = 'Sales by',
  InventoryIneligible = 'Inv. Elig.',
  ARIneligible = 'AR Elig.',
  InventorySKU = 'Inv. SKU Elig.',
  InventoryCustomRules = 'Inv. Custom',
  IneligibleARTable = 'Inel. AR',
  IneligibleInventoryTable = 'Inel. Inv.',
  IneligibleInventoryReason = 'Inel. Inv. Reason',
  IneligibleInventoryDetail = 'Inel. Inv. Detail',
  IneligibleARDetails = 'Inel. AR Detail',
  IneligibleARDetailsInvoices = 'Inel. AR Inv.',
  MasterInvoices = 'Invoices',
  ClientFundingsTable = 'Client Collateral',
  // Loan Committee Report exhibits. The value is what a reader sees beside the
  // note, so it names the table rather than abbreviating it — and it is the
  // stored `tableType`, so it must stay unique and must not be renamed once
  // notes exist against it.
  LcrBoardMembers = 'Board Members',
  LcrExistingDebt = 'Existing Debt',
  LcrInventoryAppraisal = 'Inventory Appraisal',
  LcrLiquidationChannels = 'Liquidation Channels',
  LcrArLedgerActivity = 'AR Ledger Activity',
  LcrCustomerInvoicing = 'Customer Invoicing',
  CapTable = 'Cap Table',
}

export const ONGOING_REPORTING_CELL_NAME_MAPPING: Partial<
  Record<OngoingReportingType, CellNoteTableNames>
> = {
  [OngoingReportingType.IncomeStatement]: CellNoteTableNames.IncomeStatement,
  [OngoingReportingType.BalanceSheet]: CellNoteTableNames.BalanceSheet,
  [OngoingReportingType.IncomeStatementProjections]: CellNoteTableNames.IncomeStatement,
  [OngoingReportingType.BalanceSheetProjections]: CellNoteTableNames.BalanceSheet,
}

export const CLIENT_CASH_FLOW_CELL_NAME_MAPPING = CellNoteTableNames.CashFlow

export interface CellNoteContext {
  tableType: CellNoteTableNames
  rowId?: string
  columnId?: string
  value?: string
  recordDate?: string
  periodView?: ReportingPeriods
  // Optional entity IDs to link the note to entities via Note_mentions
  entityId?: string
  clientId?: string
  userId?: string
  prospectEntityId?: string
}

// Helper to create cell key from cell coordinates.
export const getCellKey = (rowId: string, columnId: string, recordDate?: string | null): string => {
  if (recordDate === undefined || recordDate === null) {
    return `${rowId || ''}|${columnId || ''}`
  }
  return `${rowId || ''}|${columnId || ''}|${recordDate}`
}

/**
 * The month labels a `MMM YYYY` cell-note columnId is built from. Exported
 * because the same labels have to generate those ids and expand them here — two
 * copies drifting would make monthly notes invisible in rolled-up views.
 */
export const PERIOD_LOOKUP_MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const QUARTER_TO_MONTH_INDICES: Record<number, number[]> = {
  1: [0, 1, 2],
  2: [3, 4, 5],
  3: [6, 7, 8],
  4: [9, 10, 11],
}

/**
 * Returns the list of column IDs to check when looking up cell notes for a given column in a period view.
 * - Monthly: only the column itself
 * - Quarterly: column (e.g. "Q1 2023" or "Q3 2025 Proj") plus its months
 * - Annually: column (e.g. "2023" or "2025 Proj") plus all quarters and months in that year
 * Optional trailing " <suffix>" (e.g. Proj, YTD, QTD, w/ Proj) is captured and re-appended to expanded IDs.
 */
export const getColumnIdsForPeriodLookup = (
  columnId: string,
  periodView: ReportingPeriods,
): string[] => {
  if (
    !columnId ||
    (periodView !== ReportingPeriods.Quarterly && periodView !== ReportingPeriods.Annually)
  ) {
    return [columnId]
  }

  if (periodView === ReportingPeriods.Quarterly) {
    const quarterMatch = columnId.match(/^Q([1-4]) (\d{4})(?:\s+(.+))?$/)
    if (quarterMatch) {
      const quarterNum = parseInt(quarterMatch[1], 10)
      const year = quarterMatch[2]
      const suffix = quarterMatch[3] ? ` ${quarterMatch[3]}` : ''
      const monthIndices = QUARTER_TO_MONTH_INDICES[quarterNum] ?? []
      const monthIds = monthIndices.map((i) => {
        const id = `${PERIOD_LOOKUP_MONTH_LABELS[i]} ${year}`
        return suffix ? `${id}${suffix}` : id
      })
      return [columnId, ...monthIds]
    }
    return [columnId]
  }

  if (periodView === ReportingPeriods.Annually) {
    const yearMatch = columnId.match(/^(\d{4})(?:\s+(.+))?$/)
    if (yearMatch) {
      const year = yearMatch[1]
      const suffix = yearMatch[2] ? ` ${yearMatch[2]}` : ''
      const ytdSuffix = ' YTD'

      const quarterIdsFY = ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${q} ${year}`)
      const monthIdsFY = PERIOD_LOOKUP_MONTH_LABELS.map((m) => `${m} ${year}`)

      // Empty and YTD share the same notes
      if (suffix === '' || suffix === ytdSuffix) {
        const yearIds = [year, `${year}${ytdSuffix}`]
        return [...yearIds, ...quarterIdsFY, ...monthIdsFY]
      }

      // Other suffixes keep distinct and apply to quarters/months
      const quarterIds = ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${q} ${year}${suffix}`.trim())
      const monthIds = PERIOD_LOOKUP_MONTH_LABELS.map((m) => `${m} ${year}${suffix}`.trim())
      return [columnId, ...quarterIds, ...monthIds]
    }
    return [columnId]
  }

  return [columnId]
}

/**
 * Returns column IDs to use for the notes list API when in cell notes mode.
 * Uses period roll-up when periodView is provided; otherwise just the single columnId.
 */
export const getColumnIdsForCellNotesList = (
  columnId: string | undefined,
  periodView: ReportingPeriods | undefined,
): string[] => {
  if (!columnId) return []
  if (periodView) return getColumnIdsForPeriodLookup(columnId, periodView)
  return [columnId]
}
